export type ConnectableSocket = {
  connected: boolean;
  connect: () => unknown;
  off: (event: 'connect', listener: () => void) => unknown;
  on: (event: 'connect', listener: () => void) => unknown;
};

const SOCKET_DISCOVERY_INTERVAL_MS = 25;

export function waitForConnectedSocket<TSocket extends ConnectableSocket>(
  getSocket: () => TSocket | null,
  timeoutMs: number,
): Promise<TSocket | null> {
  return new Promise((resolve) => {
    let candidate: TSocket | null = null;
    let discoveryTimer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const finish = (socket: TSocket | null) => {
      if (settled) return;
      settled = true;
      if (discoveryTimer !== null) clearTimeout(discoveryTimer);
      clearTimeout(timeoutTimer);
      candidate?.off('connect', handleConnect);
      resolve(socket);
    };

    const handleConnect = () => finish(candidate);

    const discoverSocket = () => {
      candidate = getSocket();
      if (!candidate) {
        discoveryTimer = setTimeout(discoverSocket, SOCKET_DISCOVERY_INTERVAL_MS);
        return;
      }
      if (candidate.connected) {
        finish(candidate);
        return;
      }
      candidate.on('connect', handleConnect);
      candidate.connect();
      // Covers socket implementations that connect synchronously.
      if (candidate.connected) finish(candidate);
    };

    const timeoutTimer = setTimeout(() => finish(null), timeoutMs);
    discoverSocket();
  });
}
